import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../StyledCss/styledInputs";
import { addEmployee, getReportingCandidates, getAssignableRoles } from "../../api/services/hroneApi";
import { parseResponseBody } from "../../util/apiResponse";
import { isHiddenRole } from "../../util/roleConstants";

import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import "./addEmployee.css";
import { ErrorText } from "../../StyledCss/styledComponents";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

const AddEmployeePopup = (props) => {
  const { toggleIsPopupOpen, UpdateEmpAdded, fetchEmployees, closeBtn, fetchLeads } =
    props;
  const { hasPermission } = usePermissions();
  const canViewSalary = hasPermission(FEATURE_CODES.EMPLOYEE_SALARY_VIEW);
  const [showPswd, setShowPswd] = useState(false);
  const [assignableRoles, setAssignableRoles] = useState([]);
  // Role names offered in the "Reporting To" role dropdown — every role whose
  // show_reporting_to_role flag is on, most senior first.
  const [reportingToRoleNames, setReportingToRoleNames] = useState([]);

  useEffect(() => {
    const loadAssignableRoles = async () => {
      try {
        const response = await getAssignableRoles();
        const { ok, data } = await parseResponseBody(response);
        if (!ok || !data?.roles) return;
        // Which roles this user may hand out is decided by the backend from
        // role_assignable_roles (Role Permissions -> "Roles this role can
        // assign"), not by rank any more — the server flags each role it
        // returns.
        setAssignableRoles(
          data.roles.filter(
            (r) => r.is_assignable_by_current_role && !isHiddenRole(r.name),
          )
        );
        // Deliberately NOT rank-filtered: which roles can be reported to is
        // decided by each role's own show_reporting_to_role flag (set on the
        // Role Permissions screen), and a reporting manager is normally more
        // senior than any role the current user is allowed to assign.
        setReportingToRoleNames(
          [...data.roles]
            .filter((r) => r.show_reporting_to_role && !isHiddenRole(r.name))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((r) => r.name)
        );
      } catch (err) {
        // leave assignableRoles empty; the role dropdown just won't populate
      }
    };
    loadAssignableRoles();
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [dropdown, setDropDown] = useState("none");
  const [inValidDataErrorMsg, setInvalidError] = useState("");
  const [certificatesList, setCertificatesList] = useState({});
  const [reportingCandidates, setReportingCandidates] = useState([]);
  const [reportingRole, setReportingRole] = useState("");

  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchReportingCandidates = async () => {
      try {
        const response = await getReportingCandidates();
        if (response.status === 200) {
          const data = await response.json();
          setReportingCandidates(Array.isArray(data) ? data : []);
        }
      } catch (error) {}
    };
    fetchReportingCandidates();
  }, []);

  const reportingEmailsForRole = reportingCandidates
    .filter((candidate) => candidate.role === reportingRole)
    .map((candidate) => candidate.email);

  const onChangeReportingRole = (e) => {
    setReportingRole(e.target.value);
    setValue("reportingTo", "");
  };


  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropDown("none");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onSubmit = (data) => {
    handleAddEmployeeForm(data);
  };

  const onSelectCertificates = (e) => {
    const name = e.target.name;

    if (e.target.checked) {
      setCertificatesList({ ...certificatesList, [name]: true });
    } else {
      delete certificatesList[name];
      setCertificatesList({ ...certificatesList });
    }
  };

  const handleAddEmployeeForm = async (data) => {
    try {
      const response = await addEmployee(data, certificatesList);

      if (response.status === 200) {
        UpdateEmpAdded();
        toggleIsPopupOpen(); // to update popup open / close
        fetchEmployees(); // calling the fetch employee to refresh data
        fetchLeads();
      } else {
        const data = await response.json();
        setInvalidError(data.detail);
      }
    } catch (error) {}
  };

  const toggleDropdown = () => {
    if (dropdown === "none") {
      setDropDown("block");
    } else {
      setDropDown("none");
    }
  };

  // const orgDetails = JSON.parse(localStorage.getItem("org"));
  // const { primaryColor } = orgDetails;

  return (
    <div className="popup-content">
      <form
        autoComplete="off"
        className="add-employee-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="add-employee-form-container">
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.firstName && { borderLeft: "10px solid red" }}
                type="text"
                {...register("firstName", {
                  required: true,
                  pattern: /^(?!\s)[a-zA-Z\s]{3,45}$/,
                })}
              />
              <StyledLabel className="user-label">First Name*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.lastName && { borderLeft: "10px solid red" }}
                type="text"
                {...register("lastName", {
                  required: true,
                  pattern: /^(?!\s)[a-zA-Z\s]{3,45}$/,
                })}
              />
              <StyledLabel className="user-label">Last Name*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                id="employeeID"
                autoComplete="off"
                style={errors.id && { borderLeft: "10px solid red" }}
                type="text"
                {...register("id", {
                  required: true,
                  pattern: /^(?!\s)[a-zA-Z0-9]{3,10}$/,
                })}
              />
              <StyledLabel className="user-label">Employee ID*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.email && { borderLeft: "10px solid red" }}
                type="text"
                {...register("email", {
                  required: true,
                  pattern:
                    /^(?!\s)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                })}
              />
              <StyledLabel className="user-label">Email*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={errors.personalEmail && { borderLeft: "10px solid red" }}
              type="email"
              {...register("personalEmail",{
                required:true,
                pattern: /^(?!\s)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              })}
            />
            <StyledLabel className="user-label">Personal Email*</StyledLabel>
          </StyledInputContainer>

          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={errors.role && { borderLeft: "10px solid red" }}
                id="role"
                name="select"
                {...register("role", { required: true })}
              >
                <option value="">---Select Role---</option>
                {assignableRoles.map((r) => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </StyledSelect>
              <StyledLabel className="user-label">Role*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          <StyledInputGroup style={{ marginTop: "0px" }}>
            <StyledInputContainer>
              <StyledInput
                style={errors.password && { borderLeft: "10px solid red" }}
                type={showPswd ? "text" : "password"}
                autoComplete="new-password"
                {...register("password", {
                  required: true,
                  minLength: 6,
                  maxLength: 16,
                })}
              />
              <StyledLabel className="user-label">Password*</StyledLabel>
              {!showPswd ? (
                <i
                  onClick={() => setShowPswd(!showPswd)}
                  className="fa-solid fa-eye-slash  password-eye-icon"
                ></i>
              ) : (
                <i
                  onClick={() => setShowPswd(!showPswd)}
                  className="fa-solid fa-eye password-eye-icon"
                ></i>
              )}
            </StyledInputContainer>
          </StyledInputGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.dob && { borderLeft: "10px solid red" }}
                type="date"
                max="9999-12-31"
                {...register("dob", { required: true })}
              />
              <StyledLabel hasValue={true} className="user-label">
                DOB*
              </StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.joiningDate && { borderLeft: "10px solid red" }}
                type="date"
                max="9999-12-31"
                // max={new Date().toISOString().split("T")[0]}
                {...register("joiningDate", { required: true })}
              />
              <StyledLabel hasValue={true} className="user-label">
                Joining Date*
              </StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
              <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput

                style={errors.pastExperience && { borderLeft: "10px solid red" }}
                type="number"
                min={0}
                {...register("pastExperience", {
                  required: true,
                  pattern: /^[0-9]{1,3}$/,

                })}
              />
              <StyledLabel className="user-label">Past Experience (Months)*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={
                  errors.qualifications && { borderLeft: "10px solid red" }
                }
                id="qualifications"
                {...register("qualifications", { required: true })}
              >
                <option value="">---select---</option>
                <option value="Degree">Degree</option>
                <option value="B.Tech">B.Tech</option>
                <option value="PG">PG</option>
                <option value="Other">Other</option>
              </StyledSelect>
              <StyledLabel className="user-label">Qualifications*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.department && { borderLeft: "10px solid red" }}
                type="text"
                {...register("department", {
                  required: true,
                  pattern: /^(?!\s)[a-zA-Z.\s-'_]{3,60}$/,
                })}
              />
              <StyledLabel className="user-label">Department*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={errors.gender && { borderLeft: "10px solid red" }}
                id="gender"
                {...register("gender", { required: true })}
              >
                <option value="">---select---</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Others">Others</option>
              </StyledSelect>
              <StyledLabel className="user-label">Gender*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.mobileNumber && { borderLeft: "10px solid red" }}
                type="tel"
                {...register("mobileNumber", {
                  required: true,
                  maxLength: 10,
                  pattern: /[0-9]{10}/,
                })}
              />
              <StyledLabel className="user-label">Mobile No*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={errors.bloodGroup && { borderLeft: "10px solid red" }}
                id="bloodGroup"
                {...register("bloodGroup", { required: true })}
              >
                <option className="option-bg" value="">
                  ---select---
                </option>
                <option className="option-bg" value="A+">
                  A+
                </option>
                <option value="B+">B+</option>
                <option value="AB+">AB+</option>
                <option value="O+">O+</option>
                <option value="A-">A-</option>
                <option value="B-">B-</option>
                <option value="AB-">AB-</option>
                <option value="O-">O-</option>
              </StyledSelect>
              <StyledLabel className="user-label">Blood Group*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.designation && { borderLeft: "10px solid red" }}
                type="tel"
                {...register("designation", {
                  required: true,
                  // pattern: /^(?!\s)[A-Za-z.\-_ ]{2,45}$/,
                  pattern: /^(?!\s)[a-zA-Z.\s-'_ ]{3,60}$/,
                })}
              />
              <StyledLabel className="user-label">Designation*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.uan && { borderLeft: "10px solid red" }}
                type="text"
                {...register("uan", {
                  required: false,
                })}
              />
              <StyledLabel className="user-label">UAN</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

          {canViewSalary ? (
            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  style={errors.salary && { borderLeft: "10px solid red" }}
                  id="salary"
                  type="number"
                  min="0"
                  {...register("salary", { required: true })}
                />
                <StyledLabel className="user-label">Salary*</StyledLabel>
              </StyledInputContainer>
            </StyledInputGroup>
          ) : null}

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={errors.location && { borderLeft: "10px solid red" }}
                type="text"
                {...register("location", {
                  required: true,
                  pattern: /^(?!\s)[A-Za-z ]{3,45}$/i,
                })}
              />
              <StyledLabel className="user-label">Location*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>

            <StyledSelectGroup>
              <StyledSelectContainer>
                <StyledSelect
                  id="reportingToRole"
                  value={reportingRole}
                  onChange={onChangeReportingRole}
                >
                  <option className="option-bg" value="">
                    ---select---
                  </option>
                  {reportingToRoleNames.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </StyledSelect>
                <StyledLabel className="user-label">Reporting To Role*</StyledLabel>
              </StyledSelectContainer>
            </StyledSelectGroup>

            <StyledSelectGroup>
              <StyledSelectContainer>
                <StyledSelect
                  style={errors.reportingTo && { borderLeft: "10px solid red" }}
                  id="reportingTo"
                  {...register("reportingTo", { required: true })}
                >
                  <option className="option-bg" value="">
                    ---select---
                  </option>
                  {reportingEmailsForRole.map((email) => (
                    <option key={email} value={email}>{email}</option>
                  ))}
                </StyledSelect>
                <StyledLabel className="user-label">Reporting To*</StyledLabel>
              </StyledSelectContainer>
            </StyledSelectGroup>


          {/* certificates */}

          <div ref={dropdownRef} className="dropdown-btn-wrap">
            <button
              className="dropdown-btn"
              onClick={toggleDropdown}
              type="button"
              style={{
                border: `1px solid #000000 `,
              }}
            >
              Certificates <MdOutlineKeyboardArrowDown />
            </button>

            <div
              className="certificates-dropdown shadow"
              style={{
                display: `${dropdown}`,
              }}
            >
              <li>
                <label htmlFor="10th">
                  <input
                    id="10th"
                    value="10th"
                    onChange={onSelectCertificates}
                    name="10th"
                    type="checkbox"
                  />{" "}
                  10th
                </label>
              </li>
              <li>
                <label>
                  <input
                    onChange={onSelectCertificates}
                    value="Intermediate"
                    type="checkbox"
                    name="Intermediate"
                  />{" "}
                  Intermediate
                </label>
              </li>

              <li>
                <label>
                  <input
                    onChange={onSelectCertificates}
                    value="Degree"
                    type="checkbox"
                    name="Degree"
                  />{" "}
                  Degree
                </label>
              </li>
              <li>
                <label>
                  <input
                    onChange={onSelectCertificates}
                    value="PG"
                    type="checkbox"
                    name="PG"
                  />{" "}
                  PG
                </label>
              </li>
              <li>
                <label>
                  <input
                    onChange={onSelectCertificates}
                    value="original Degree"
                    type="checkbox"
                    name="original Degree"
                  />{" "}
                  Original Degree
                </label>
              </li>
            </div>
          </div>
        </div>

        {inValidDataErrorMsg.length > 0 && (
          <ErrorText className="mt-2">*{inValidDataErrorMsg}</ErrorText>
        )}

        <div className="d-flex gap-2 justify-content-end mt-5">
          <SecondaryButton onClick={closeBtn} type="button">
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit">Add</PrimaryButton>
        </div>
      </form>
    </div>
  );
};

export default AddEmployeePopup;
